import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Phone, Wallet, Timer, RefreshCw, CreditCard, MapPin, Brain, Sparkles } from "lucide-react";
import StatesPicker from "../components/StatesPicker";
import AutoRefillCard from "../components/AutoRefillCard";
import { CoachSummaryCard, ObjectionHandler, CallCoachAnalysis } from "../components/CoachPanel";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusBadge({ status }) {
  const m = {
    completed: { c: "bg-emerald-100 text-emerald-800 border-emerald-200", l: "Completed" },
    ringing: { c: "bg-amber-100 text-amber-800 border-amber-200", l: "Ringing" },
    "in-progress": { c: "bg-blue-100 text-blue-800 border-blue-200", l: "Live" },
    "no-answer": { c: "bg-zinc-100 text-zinc-700 border-zinc-200", l: "No answer" },
    busy: { c: "bg-zinc-100 text-zinc-700 border-zinc-200", l: "Busy" },
    failed: { c: "bg-rose-100 text-rose-800 border-rose-200", l: "Failed" },
    canceled: { c: "bg-zinc-100 text-zinc-700 border-zinc-200", l: "Canceled" },
    no_agent: { c: "bg-rose-100 text-rose-800 border-rose-200", l: "No agent" },
  };
  const meta = m[status] || { c: "bg-zinc-100 text-zinc-700 border-zinc-200", l: status || "—" };
  return <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${meta.c}`}>{meta.l}</span>;
}

export default function AgentDashboard() {
  const { user, setUser, refresh } = useAuth();
  const [leads, setLeads] = useState([]);
  const [packages, setPackages] = useState([]);
  const [topupOpen, setTopupOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [minReadyBalance, setMinReadyBalance] = useState(450);

  const fetchLeads = useCallback(async () => {
    try {
      const { data } = await api.get("/agents/me/leads");
      setLeads(data.leads || []);
    } catch (err) {
      console.error("Failed to load leads:", err);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
    api.get("/payments/packages").then((r) => setPackages(r.data.packages || []));
    api.get("/meta/tiers").then((r) => {
      if (typeof r.data.min_ready_balance === "number") {
        setMinReadyBalance(r.data.min_ready_balance);
      }
    }).catch(() => {});
    const id = setInterval(() => {
      fetchLeads();
      refresh();
    }, 8000);
    return () => clearInterval(id);
  }, [fetchLeads, refresh]);

  if (!user) return null;

  const toggleReady = async (next) => {
    setBusy(true);
    try {
      const target = next ? "ready" : "not_ready";
      const { data } = await api.put("/agents/me/status", { status: target });
      setUser(data.user);
      toast.success(next ? "You're READY. Calls incoming." : "You're NOT READY.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const changeTier = async (tier) => {
    try {
      const { data } = await api.put("/agents/me/tier", { buffer_tier: tier });
      setUser(data.user);
      toast.success(`Buffer set to ${tier}s`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveStates = async (states) => {
    try {
      const { data } = await api.put("/agents/me/states", { states });
      setUser(data.user);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const startCheckout = async (pkgId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkgId,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const isReady = user.status === "ready" || user.status === "in_call";
  const balance = Number(user.wallet_balance || 0);
  const underMinimum = balance < minReadyBalance;
  const canGoReady = isReady || !underMinimum;

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-8">
      {/* Top KPI strip */}
      <div className="grid lg:grid-cols-12 gap-6 border border-zinc-200">
        <div className="lg:col-span-5 p-8 border-r border-zinc-200">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Operator status</div>
          <div className="mt-2 flex items-center gap-4">
            <div className="flex items-center gap-3">
              <span
                className={`inline-block w-3 h-3 rounded-full ${
                  isReady ? "bg-[#10B981] ring-pulse" : "bg-zinc-400"
                }`}
              />
              <div className="font-heading font-black text-4xl tracking-tighter">
                {isReady ? "READY" : user.status === "in_call" ? "IN CALL" : "OFFLINE"}
              </div>
            </div>
            <Switch
              checked={isReady}
              disabled={busy || !canGoReady}
              onCheckedChange={toggleReady}
              data-testid="agent-ready-status-toggle"
            />
          </div>
          {underMinimum ? (
            <div className="text-sm mt-3 border-l-2 border-rose-500 pl-3 bg-rose-50 py-2 pr-3" data-testid="min-balance-warning">
              <span className="font-bold text-rose-900">Wallet below minimum.</span>{" "}
              <span className="text-zinc-700">
                You need <span className="font-mono font-bold">${minReadyBalance.toFixed(2)}</span> to go live
                (~10 leads). Top up <span className="font-mono font-bold">${(minReadyBalance - balance).toFixed(2)}</span> more.
              </span>
            </div>
          ) : (
            <div className="text-sm text-zinc-600 mt-3">
              Toggle ON to receive inbound FEX calls on your phone. Minimum wallet: ${minReadyBalance.toFixed(2)} (~10 leads).
            </div>
          )}
        </div>
        <div className="lg:col-span-4 p-8 border-r border-zinc-200">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Wallet balance</div>
          <div className="font-mono font-bold text-5xl mt-2" data-testid="wallet-balance-value">
            ${user.wallet_balance.toFixed(2)}
          </div>
          <div
            className={`mt-2 inline-flex items-center px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] border ${
              underMinimum
                ? "bg-rose-50 text-rose-800 border-rose-300"
                : "bg-emerald-50 text-emerald-800 border-emerald-300"
            }`}
            data-testid="wallet-minimum-badge"
          >
            {underMinimum
              ? `Minimum $${minReadyBalance.toFixed(0)} to take calls`
              : `Minimum $${minReadyBalance.toFixed(0)} · Eligible`}
          </div>
          <div className="text-xs text-zinc-500 mt-2">Deducted per qualified lead</div>
          <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
            <DialogTrigger asChild>
              <Button className="mt-4 btn-sharp bg-[#0055FF] hover:bg-[#0044CC] text-white text-xs tracking-wider uppercase font-bold" data-testid="wallet-topup-button">
                <CreditCard className="w-4 h-4 mr-1" /> Top Up
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading font-black text-2xl tracking-tight">Top up wallet</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 mt-3">
                {packages.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => startCheckout(p.id)}
                    disabled={busy}
                    className="border border-zinc-300 hover:border-black bg-white p-4 text-left btn-sharp transition disabled:opacity-50"
                    data-testid={`topup-${p.id}-button`}
                  >
                    <div className="font-mono font-bold text-2xl">${p.amount}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">USD · Wallet credit</div>
                  </button>
                ))}
              </div>
              <div className="text-[11px] text-zinc-500 mt-2 font-mono uppercase tracking-wider">
                Secure checkout · 256-bit TLS · No card data stored
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <div className="lg:col-span-3 p-8">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Buffer tier</div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {[75, 115].map((t) => (
              <button
                key={t}
                onClick={() => changeTier(t)}
                className={`p-3 border btn-sharp text-left transition ${
                  user.buffer_tier === t
                    ? "border-black bg-black text-white"
                    : "border-zinc-300 hover:border-black"
                }`}
                data-testid={`agent-tier-${t}-button`}
              >
                <div className="font-mono font-bold text-xl">${t === 75 ? 40 : 60}</div>
                <div className="text-[10px] uppercase tracking-widest mt-0.5">{t}s</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid sm:grid-cols-3 gap-px bg-zinc-200 border border-t-0 border-zinc-200">
        <Kpi i={Phone} label="Total leads" value={leads.length} testid="agent-kpi-total-leads" />
        <Kpi
          i={Timer}
          label="Billable"
          value={leads.filter((l) => l.billable).length}
          testid="agent-kpi-billable"
        />
        <Kpi
          i={Wallet}
          label="Total spend"
          value={`$${leads.reduce((a, l) => a + (l.billable ? l.billed_amount : 0), 0).toFixed(2)}`}
          mono
          testid="agent-kpi-spend"
        />
      </div>

      {/* Licensed states */}
      <div className="border border-t-0 border-zinc-200 bg-white p-6" data-testid="agent-states-section">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-zinc-500" />
            <div className="font-heading font-bold tracking-tight text-lg">Licensed states</div>
          </div>
          <div className="text-xs text-zinc-500">
            Calls only route to you when the caller's state is selected below.
          </div>
        </div>
        <StatesPicker
          value={user.states || []}
          onChange={saveStates}
          testIdPrefix="dashboard-states"
        />
        {(!user.states || user.states.length === 0) && (
          <div className="mt-3 text-xs font-mono uppercase tracking-widest text-rose-700">
            ⚠ No states selected — you will not receive any inbound calls.
          </div>
        )}
      </div>

      {/* Auto-refill */}
      <div className="mt-6">
        <AutoRefillCard user={user} onUpdate={setUser} />
      </div>

      {/* Tabs: Calls + AI Coach */}
      <Tabs defaultValue="calls" className="mt-6">
        <TabsList className="btn-sharp">
          <TabsTrigger value="calls" className="btn-sharp text-xs uppercase tracking-widest" data-testid="agent-tab-calls">
            <Phone className="w-3.5 h-3.5 mr-1" /> Call history
          </TabsTrigger>
          <TabsTrigger value="coach" className="btn-sharp text-xs uppercase tracking-widest" data-testid="agent-tab-coach">
            <Brain className="w-3.5 h-3.5 mr-1" /> AI Coach
          </TabsTrigger>
          <TabsTrigger value="objection" className="btn-sharp text-xs uppercase tracking-widest" data-testid="agent-tab-objection">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Objection Handler
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calls" className="mt-2">
      {/* Lead history */}
      <div className="border border-zinc-200 bg-white">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <div className="font-heading font-bold tracking-tight text-lg">Call history</div>
          <Button variant="ghost" size="sm" onClick={fetchLeads} className="btn-sharp text-xs uppercase tracking-widest" data-testid="agent-refresh-leads-button">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-[10px] uppercase tracking-widest font-bold">When</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest font-bold">Caller</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest font-bold">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest font-bold text-right">Duration</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest font-bold text-right">Buffer</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest font-bold text-right">Billed</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest font-bold text-right">AI</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest font-bold">Recording</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="agent-leads-table">
            {leads.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-zinc-500 py-8 text-sm">
                  No calls yet. Go READY to start receiving inbound FEX leads.
                </TableCell>
              </TableRow>
            )}
            {leads.map((l) => (
              <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                <TableCell className="font-mono text-xs">
                  {l.created_at ? new Date(l.created_at).toLocaleString() : "—"}
                </TableCell>
                <TableCell className="font-mono" data-testid={`lead-caller-${l.id}`}>
                  {l.masked ? (
                    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] border border-zinc-300 bg-zinc-50 text-zinc-700">
                      INBOUNDS
                    </span>
                  ) : (
                    l.customer_number
                  )}
                </TableCell>
                <TableCell>
                  <StatusBadge status={l.status} />
                </TableCell>
                <TableCell className="text-right font-mono">{l.duration}s</TableCell>
                <TableCell className="text-right font-mono text-zinc-500">{l.buffer_seconds}s</TableCell>
                <TableCell className="text-right font-mono font-bold">
                  {l.billable ? `$${l.billed_amount.toFixed(2)}` : <span className="text-zinc-400">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  {l.recording_url ? (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="btn-sharp text-xs uppercase tracking-widest"
                          data-testid={`coach-open-${l.id}`}
                        >
                          <Brain className="w-3.5 h-3.5 mr-1" />
                          {l.coach_closing_score != null ? (
                            <span className="font-mono font-bold">{l.coach_closing_score}</span>
                          ) : "View"}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="font-heading font-black text-2xl tracking-tight">
                            Call Coach Analysis
                          </DialogTitle>
                        </DialogHeader>
                        <CallCoachAnalysis leadId={l.id} />
                      </DialogContent>
                    </Dialog>
                  ) : (
                    <span className="text-[11px] font-mono text-zinc-400 uppercase tracking-widest">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {l.recording_url ? (
                    <audio
                      controls
                      preload="none"
                      src={`${API_BASE}/leads/${l.id}/recording.mp3`}
                      className="h-8 max-w-[200px]"
                      data-testid={`lead-recording-${l.id}`}
                    >
                      <track kind="captions" />
                    </audio>
                  ) : (
                    <span className="text-[11px] font-mono text-zinc-400 uppercase tracking-widest">—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
        </TabsContent>

        <TabsContent value="coach" className="mt-2">
          <CoachSummaryCard />
        </TabsContent>

        <TabsContent value="objection" className="mt-2">
          <ObjectionHandler />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Kpi({ i: Icon, label, value, mono, testid }) {
  return (
    <div className="bg-white p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">{label}</div>
        <Icon className="w-4 h-4 text-zinc-400" />
      </div>
      <div className={`mt-2 ${mono ? "font-mono" : "font-heading"} font-black text-3xl tracking-tight`}>{value}</div>
    </div>
  );
}
